'use client';
import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import WhatsAppButton from '@/components/WhatsAppButton';
import { createClient } from '@/lib/supabase/client';

interface WorkingHoursDay {
  enabled?: boolean;
  start?: string;
  end?: string;
}

interface WorkingHours {
  [day: string]: WorkingHoursDay;
}

interface OpeningHoursEntry {
  day: string;
  start: string;
  end: string;
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, visible };
}

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openingHours, setOpeningHours] = useState<OpeningHoursEntry[]>([]);
  const [hoursLoading, setHoursLoading] = useState(true);

  const hero = useReveal(0);
  const formCard = useReveal(0);
  const infoCard = useReveal(150);
  const hoursCard = useReveal(300);
  const whatsappCard = useReveal(450);

  useEffect(() => {
    async function fetchWorkingHours() {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('instructor_profiles')
          .select('working_hours')
          .ilike('first_name', 'stephen')
          .ilike('last_name', 'simson')
          .single();

        if (error || !data) {
          setOpeningHours([
            { day: 'Maandag', start: '13:30', end: '18:00' },
            { day: 'Dinsdag', start: '10:30', end: '19:30' },
            { day: 'Woensdag', start: '10:30', end: '19:30' },
            { day: 'Donderdag', start: '10:30', end: '19:30' },
            { day: 'Vrijdag', start: '10:30', end: '19:30' },
            { day: 'Zaterdag', start: '10:30', end: '19:30' },
          ]);
          return;
        }

        const wh: WorkingHours = data.working_hours || {};
        const dayNames: { [key: string]: string } = {
          monday: 'Maandag',
          tuesday: 'Dinsdag',
          wednesday: 'Woensdag',
          thursday: 'Donderdag',
          friday: 'Vrijdag',
          saturday: 'Zaterdag',
          sunday: 'Zondag',
        };

        const orderedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const entries: OpeningHoursEntry[] = [];

        orderedDays.forEach(day => {
          const d = wh[day];
          if (d && d.enabled && d.start && d.end) {
            entries.push({ day: dayNames[day], start: d.start, end: d.end });
          }
        });

        if (entries.length === 0) {
          setOpeningHours([
            { day: 'Maandag', start: '13:30', end: '18:00' },
            { day: 'Dinsdag', start: '10:30', end: '19:30' },
            { day: 'Woensdag', start: '10:30', end: '19:30' },
            { day: 'Donderdag', start: '10:30', end: '19:30' },
            { day: 'Vrijdag', start: '10:30', end: '19:30' },
            { day: 'Zaterdag', start: '10:30', end: '19:30' },
          ]);
        } else {
          setOpeningHours(entries);
        }
      } catch {
        setOpeningHours([
          { day: 'Maandag', start: '13:30', end: '18:00' },
          { day: 'Dinsdag', start: '10:30', end: '19:30' },
          { day: 'Woensdag', start: '10:30', end: '19:30' },
          { day: 'Donderdag', start: '10:30', end: '19:30' },
          { day: 'Vrijdag', start: '10:30', end: '19:30' },
          { day: 'Zaterdag', start: '10:30', end: '19:30' },
        ]);
      } finally {
        setHoursLoading(false);
      }
    }
    fetchWorkingHours();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error: supabaseError } = await supabase
        .from('contact_submissions')
        .insert({
          voornaam: formData.firstName,
          achternaam: formData.lastName,
          email: formData.email,
          telefoon: formData.phone || null,
          bericht: formData.message,
        });

      if (supabaseError) {
        setError('Er is iets misgegaan. Probeer het opnieuw.');
        console.error('Contact form error:', supabaseError.message);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError('Er is iets misgegaan. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="font-sans bg-white">
      <Header />

      {/* ── HERO ── */}
      <section className="bg-gray-950 overflow-hidden relative">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand" />
        <div
          ref={hero.ref}
          className="max-w-screen-xl mx-auto px-6 md:px-16 py-20 md:py-32 transition-all duration-700 ease-out"
          style={{ opacity: hero.visible ? 1 : 0, transform: hero.visible ? 'translateY(0)' : 'translateY(48px)' }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center text-sm space-x-1.5 text-gray-600 mb-8">
            <a href="/" aria-label="Homepage" className="hover:text-gray-300 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z" clipRule="evenodd" />
              </svg>
            </a>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 opacity-40 text-gray-500">
              <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-600">Contact</span>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <span className="w-10 h-px bg-brand" />
            <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Neem contact op</span>
          </div>

          <h1 className="font-black text-white leading-none mb-6" style={{ fontSize: 'clamp(3rem, 8vw, 7rem)' }}>
            Hoe kunnen we<br />
            <span style={{ color: 'transparent', WebkitTextStroke: '2px #D4AF37' }}>je helpen?</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-lg leading-relaxed">
            Stel je vraag, plan een proefles of vraag meer informatie aan. We reageren zo snel mogelijk.
          </p>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="bg-white py-20 md:py-32 px-6 md:px-16">
        <div className="max-w-screen-xl mx-auto grid md:grid-cols-5 gap-8 md:gap-12">

          {/* ── FORM ── */}
          <div
            ref={formCard.ref}
            className="md:col-span-3 transition-all duration-700 ease-out"
            style={{ opacity: formCard.visible ? 1 : 0, transform: formCard.visible ? 'translateX(0)' : 'translateX(-60px)' }}
          >
            <div className="border border-gray-100 p-8 md:p-12">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-brand/10 flex items-center justify-center mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-8 h-8 text-brand">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3">Bericht ontvangen!</h3>
                  <p className="text-gray-500 text-sm max-w-xs">We nemen zo snel mogelijk contact met je op. Bedankt voor je bericht.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="w-10 h-px bg-brand" />
                    <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Stuur een bericht</span>
                  </div>
                  <h2 className="font-black text-gray-900 text-3xl md:text-4xl leading-none mb-10">Schrijf ons</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Voornaam</label>
                        <input
                          className="w-full border-b-2 border-gray-200 bg-transparent h-12 px-0 text-sm focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-300"
                          placeholder="Jan"
                          required
                          autoComplete="off"
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Achternaam</label>
                        <input
                          className="w-full border-b-2 border-gray-200 bg-transparent h-12 px-0 text-sm focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-300"
                          placeholder="de Vries"
                          required
                          autoComplete="off"
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">E-mailadres</label>
                      <input
                        className="w-full border-b-2 border-gray-200 bg-transparent h-12 px-0 text-sm focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-300"
                        placeholder="jouw@email.nl"
                        required
                        autoComplete="off"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">
                        Telefoonnummer <span className="text-gray-300 normal-case font-normal">(optioneel)</span>
                      </label>
                      <input
                        className="w-full border-b-2 border-gray-200 bg-transparent h-12 px-0 text-sm focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-300"
                        placeholder="06 12345678"
                        autoComplete="off"
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">Jouw bericht</label>
                      <textarea
                        className="w-full border-b-2 border-gray-200 bg-transparent px-0 py-3 text-sm focus:border-gray-900 focus:outline-none transition-colors h-32 resize-none placeholder-gray-300"
                        placeholder="Schrijf hier je vraag of opmerking..."
                        required
                        autoComplete="off"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>

                    {error && (
                      <p className="text-red-600 text-sm bg-red-50 px-4 py-3">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gray-950 hover:bg-gray-800 active:scale-95 text-white font-black h-14 text-sm uppercase tracking-widest transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 mt-4"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Versturen...
                        </>
                      ) : (
                        <>
                          Verstuur bericht
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                          </svg>
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* ── SIDEBAR ── */}
          <div className="md:col-span-2 space-y-5">

            {/* Contact details */}
            <div
              ref={infoCard.ref}
              className="bg-gray-950 text-white p-8 transition-all duration-700 ease-out"
              style={{ opacity: infoCard.visible ? 1 : 0, transform: infoCard.visible ? 'translateX(0)' : 'translateX(60px)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-6 h-px bg-brand" />
                <span className="text-brand text-xs font-bold uppercase tracking-[0.2em]">Contactgegevens</span>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-brand">
                      <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Telefoon</div>
                    <a href="tel:0620564200" className="text-white font-bold hover:text-brand transition-colors">06 20564200</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-brand">
                      <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
                      <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">E-mail</div>
                    <a href="mailto:info@rijschoolsimson.nl" className="text-white font-bold hover:text-brand transition-colors text-sm">info@rijschoolsimson.nl</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-brand">
                      <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Locatie</div>
                    <span className="text-white font-bold">Almere</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-gray-600 space-y-1">
                <div>KvK: 67021905</div>
                <div>BTW: NL002420936B80</div>
                <div>Rijschoolnummer: 2027N0</div>
              </div>
            </div>

            {/* Opening hours */}
            <div
              ref={hoursCard.ref}
              className="border border-gray-100 p-8 transition-all duration-700 ease-out"
              style={{ opacity: hoursCard.visible ? 1 : 0, transform: hoursCard.visible ? 'translateX(0)' : 'translateX(60px)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-6 h-px bg-brand" />
                <span className="text-brand text-xs font-bold uppercase tracking-[0.2em]">Openingstijden</span>
              </div>
              {hoursLoading ? (
                <div className="space-y-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-4 bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {openingHours.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0">
                      <span className="text-gray-700 font-semibold">{entry.day}</span>
                      <span className="text-gray-500 font-medium">{entry.start} – {entry.end}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* WhatsApp CTA */}
            <a
              ref={whatsappCard.ref as React.RefObject<HTMLAnchorElement>}
              href="https://wa.me/31620564200"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-green-500 hover:bg-green-600 active:scale-95 transition-all duration-150 p-6 text-white block"
              style={{ opacity: whatsappCard.visible ? 1 : 0, transform: whatsappCard.visible ? 'translateX(0)' : 'translateX(60px)', transition: 'opacity 0.7s ease 0.45s, transform 0.7s ease 0.45s, background-color 0.15s' }}
            >
              <div className="w-10 h-10 bg-white/20 flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                </svg>
              </div>
              <div>
                <div className="font-black text-sm uppercase tracking-widest">Direct WhatsAppen</div>
                <div className="text-green-100 text-xs mt-0.5">0620564200</div>
              </div>
            </a>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </main>
  );
}
